import { useEffect, useState } from "react";
import { LogOut, ChevronDown, Menu, Settings, Bell } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { useNavigate } from "react-router-dom";

export function Topbar({ title, subtitle, actions, onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!user) return undefined;
    const load = () => {
      api.get("/notifications/summary", { skipGlobalLoader: true })
        .then(({ data }) => {
          if (!alive) return;
          setUnread(Number(data?.unread || 0));
        })
        .catch(() => {});
    };
    load();
    window.addEventListener("hx:notifications-updated", load);
    return () => {
      alive = false;
      window.removeEventListener("hx:notifications-updated", load);
    };
  }, [user]);

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const roleLabel = {
    super_admin: "Super Admin",
    advertiser_admin: "Admin",
    employee: "Employee",
  }[user?.role] || "User";

  return (
    <header
      className="sticky top-0 z-30 flex-shrink-0"
      style={{
        background: "var(--hx-topbar)",
        backdropFilter: "blur(16px) saturate(180%)",
        WebkitBackdropFilter: "blur(16px) saturate(180%)",
        borderBottom: "1px solid var(--hx-topbar-border)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 3px rgba(15,23,42,0.04)",
      }}
      data-testid="topbar"
    >
      <div className="hx-topbar-inner flex items-center justify-between px-3 md:px-6 h-14 gap-2 sm:gap-4 min-w-0">

        {/* Left: Menu toggle + Title */}
        <div className="hx-topbar-title-group flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <button
            onClick={onMenuClick}
            className="p-2 -ml-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-200 transition-all duration-150 flex-shrink-0"
            aria-label="Toggle menu"
            type="button"
          >
            <Menu size={18} strokeWidth={2} />
          </button>

          <div className="w-px h-5 bg-slate-200 hidden sm:block flex-shrink-0" />

          <div className="min-w-0 block">
            {title && (
              <h1 className="text-[15px] font-bold text-slate-900 leading-tight tracking-tight truncate">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="text-[11px] text-slate-500 leading-tight mt-0.5 truncate">{subtitle}</p>
            )}
          </div>
        </div>

        {/* Right: Actions + Profile */}
        <div className="hx-topbar-right flex min-w-0 flex-shrink-0 items-center gap-1.5 sm:gap-2">
          <div className="hx-topbar-actions flex min-w-0 shrink items-center gap-1.5 sm:gap-2 overflow-hidden">
            {actions}
          </div>

          <button
            type="button"
            onClick={() => navigate(user?.role === "super_admin" ? "/super-admin/notifications" : "/notifications")}
            className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-transparent text-slate-600 transition-all duration-150 hover:border-slate-200/80 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Notifications"
          >
            <Bell size={17} />
            {unread > 0 && (
              <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>

          {/* Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="topbar-profile"
                className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-xl hover:bg-slate-100 transition-all duration-150 group border border-transparent hover:border-slate-200/80 focus:outline-none"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback
                    className="text-white text-[11px] font-bold"
                    style={{ background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)" }}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-[13px] font-semibold text-slate-800 leading-tight whitespace-nowrap">
                    {user?.name?.split(" ")[0] || "User"}
                  </div>
                  <div className="text-[10px] text-slate-500 leading-tight">
                    {roleLabel}
                  </div>
                </div>
                <ChevronDown
                  size={13}
                  className="text-slate-400 hidden sm:block transition-transform duration-200 group-data-[state=open]:rotate-180"
                />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              className="w-56 rounded-xl shadow-xl border-slate-200/80 p-1"
              style={{ boxShadow: "0 8px 24px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.08)" }}
            >
              <DropdownMenuLabel className="px-3 py-2.5">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)" }}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-[13px] truncate">{user?.name || "User"}</div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">{user?.email}</div>
                  </div>
                </div>
              </DropdownMenuLabel>
              {user?.role !== "super_admin" && (
                <>
                  <DropdownMenuSeparator className="bg-slate-100 my-1" />
                  <DropdownMenuItem
                    onClick={() => navigate("/profile")}
                    data-testid="profile-menu-settings"
                    className="rounded-lg gap-2.5 text-slate-700 cursor-pointer text-[13px] py-2"
                  >
                    <Settings size={14} className="text-slate-400 flex-shrink-0" />
                    Account Settings
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator className="bg-slate-100 my-1" />
              <DropdownMenuItem
                onClick={async () => { await logout(); navigate("/"); }}
                className="rounded-lg gap-2.5 text-rose-600 focus:text-rose-700 focus:bg-rose-50 cursor-pointer text-[13px] py-2"
                data-testid="profile-menu-logout"
              >
                <LogOut size={14} className="flex-shrink-0" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
